import { useQuery } from "@tanstack/react-query";
import { runsOnDevice } from "../api/client.js";

/** One creator's answer from the server (services/api/src/social-youtube.ts). */
export interface CreatorVideo {
  videoId: string;
  title: string;
  publishedAt: string;
  thumbnail: string;
}

export interface CreatorChannel {
  id: string;
  title: string;
  latest: CreatorVideo | null;
  live: CreatorVideo | null;
}

export interface CreatorsAnswer {
  channels: CreatorChannel[];
  /** False until the server has a YouTube key: no LIVE tags to show. */
  liveChecked: boolean;
}

/**
 * Where the Social route lives. The site calls its own server; the APK has no
 * server behind /api (api/deviceApi.ts), so it calls ours by name — the one
 * route the app asks of sliquetv.online, since the YouTube key may only live
 * there (owner, 2026-10-07: "our server"). CapacitorHttp carries the request,
 * so the page's origin does not matter.
 */
const SERVER = "https://sliquetv.online";

export function creatorsUrl(channelIds: string[]): string {
  const path = `/api/social/youtube?channels=${channelIds.join(",")}`;
  return runsOnDevice() ? SERVER + path : path;
}

/** The most channels one server call takes (services/api/src/social-youtube.ts MAX_CHANNELS). */
const BATCH = 50;

/** How often the page asks again while open: the server's own live check runs every 3 minutes. */
const REFRESH_MS = 3 * 60 * 1000;

export function useCreators(channelIds: string[]) {
  const key = channelIds.join(",");
  return useQuery({
    queryKey: ["social", "youtube", key],
    queryFn: async (): Promise<CreatorsAnswer> => {
      // The server answers at most 50 channels a call: a profile's subscriptions go in batches.
      const channels: CreatorChannel[] = [];
      let liveChecked = true;
      for (let i = 0; i < channelIds.length; i += BATCH) {
        const response = await fetch(creatorsUrl(channelIds.slice(i, i + BATCH)));
        if (!response.ok) throw new Error(`Social ${response.status}`);
        const answer = (await response.json()) as CreatorsAnswer;
        channels.push(...answer.channels);
        liveChecked = liveChecked && answer.liveChecked;
      }
      return { channels, liveChecked };
    },
    enabled: channelIds.length > 0,
    staleTime: REFRESH_MS,
    refetchInterval: REFRESH_MS,
    retry: 1,
  });
}

/** The YouTube page of a video; the embedded player is built from it (youtubeEmbedUrl). */
export function youtubeWatchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`;
}
